/**
 * Cancellation Retention Modal
 *
 * Shown when a trialing user clicks cancel. Offers 50% off first month
 * to convert them to a paying customer.
 *
 * Actions:
 * - Accept: End trial, apply 50% off coupon → active subscription
 * - Decline: Cancel trial immediately → back to free tier
 * - Close (X): Nothing happens, stays on trial
 */

import React from 'react';
import { X, Sparkles, Gift, XCircle, Loader2 } from 'lucide-react';
import { useAcceptRetentionOffer, useDeclineRetentionOffer } from '../../hooks';

export default function CancellationRetentionModal({ isOpen, onClose, subscription }) {
  const acceptMutation = useAcceptRetentionOffer();
  const declineMutation = useDeclineRetentionOffer();

  if (!isOpen) return null;

  const originalPrice = subscription?.amount ? `$${(subscription.amount / 100).toFixed(0)}` : '$300';
  const discountedPrice = subscription?.amount ? `$${(subscription.amount / 200).toFixed(0)}` : '$150';
  const planName = subscription?.plan_name || 'Professional';
  const isPending = acceptMutation.isPending || declineMutation.isPending;

  const handleAccept = async () => {
    try {
      await acceptMutation.mutateAsync();
      onClose();
    } catch (error) {
      console.error('Failed to accept retention offer:', error);
    }
  };

  const handleDecline = async () => {
    try {
      await declineMutation.mutateAsync();
      onClose();
    } catch (error) {
      console.error('Failed to decline retention offer:', error);
    }
  };

  return (
    <div className="retention-modal-overlay" onClick={onClose}>
      <style>{`
        .retention-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: retentionFadeIn 0.2s ease;
        }

        @keyframes retentionFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .retention-modal {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 16px;
          width: 100%;
          max-width: 440px;
          margin: 16px;
          overflow: hidden;
          animation: retentionSlideUp 0.3s ease;
        }

        @keyframes retentionSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .retention-header {
          padding: 24px 24px 0;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }

        .retention-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .retention-icon svg {
          width: 24px;
          height: 24px;
        }

        .retention-close {
          background: none;
          border: none;
          color: var(--text-tertiary, #6b7280);
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .retention-close:hover {
          background: var(--bg-tertiary, #1a1d24);
          color: var(--text-primary, #f0f2f5);
        }

        .retention-content {
          padding: 20px 24px 24px;
        }

        .retention-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 8px;
        }

        .retention-description {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
          line-height: 1.5;
          margin: 0 0 20px;
        }

        .retention-offer-card {
          background: var(--bg-tertiary, #1a1d24);
          border: 1px solid #f59e0b;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          text-align: center;
        }

        .offer-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .offer-badge svg {
          width: 14px;
          height: 14px;
        }

        .offer-pricing {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .offer-original-price {
          font-size: 18px;
          color: var(--text-tertiary, #6b7280);
          text-decoration: line-through;
        }

        .offer-new-price {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary, #f0f2f5);
        }

        .offer-period {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
        }

        .offer-detail {
          font-size: 13px;
          color: var(--text-tertiary, #6b7280);
          margin-top: 4px;
        }

        .retention-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .retention-btn {
          width: 100%;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: inherit;
        }

        .retention-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .retention-btn.accept {
          background: #f59e0b;
          border: none;
          color: white;
        }

        .retention-btn.accept:hover:not(:disabled) {
          background: #d97706;
        }

        .retention-btn.decline {
          background: transparent;
          border: 1px solid var(--border-primary, #23262f);
          color: var(--text-secondary, #8b919e);
        }

        .retention-btn.decline:hover:not(:disabled) {
          background: var(--bg-tertiary, #1a1d24);
          color: var(--text-primary, #f0f2f5);
        }

        .retention-btn svg {
          width: 16px;
          height: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .retention-btn .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div className="retention-modal" onClick={(e) => e.stopPropagation()}>
        <div className="retention-header">
          <div className="retention-icon">
            <Gift />
          </div>
          <button className="retention-close" onClick={onClose} disabled={isPending}>
            <X />
          </button>
        </div>

        <div className="retention-content">
          <h2 className="retention-title">Before you go...</h2>
          <p className="retention-description">
            We'd hate to see you leave! How about 50% off your first month of {planName}?
          </p>

          <div className="retention-offer-card">
            <div className="offer-badge">
              <Sparkles />
              Special Offer
            </div>
            <div className="offer-pricing">
              <span className="offer-original-price">{originalPrice}</span>
              <span className="offer-new-price">{discountedPrice}</span>
              <span className="offer-period">/first month</span>
            </div>
            <div className="offer-detail">
              Then {originalPrice}/month after. Cancel anytime.
            </div>
          </div>

          <div className="retention-actions">
            <button
              className="retention-btn accept"
              onClick={handleAccept}
              disabled={isPending}
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="spinning" />
                  Applying offer...
                </>
              ) : (
                <>
                  <Gift />
                  Get 50% Off First Month
                </>
              )}
            </button>

            <button
              className="retention-btn decline"
              onClick={handleDecline}
              disabled={isPending}
            >
              {declineMutation.isPending ? (
                <>
                  <Loader2 className="spinning" />
                  Canceling...
                </>
              ) : (
                <>
                  <XCircle />
                  No thanks, cancel my trial
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
